import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Lightbulb, PenTool, Users, Megaphone, Share2, Video, BarChart3, Palette } from "lucide-react";
import EditorialSteps from "@/components/EditorialSteps";
import TechnologyStack from "@/components/TechnologyStack";
import ServicesGrid from "@/components/ServicesGrid";
import { usePageContent } from "@/hooks/usePageContent";
import { 
  heroVariant, sectionVariant, staggerContainer, itemVariant,
  viewportOnce, glowVariant,
  problemSectionVariant, problemStaggerContainer, problemAccentLineVariant,
  problemBulletVariant, problemHeaderVariant,
} from "@/lib/motion";

const iconMap: Record<string, any> = { Lightbulb, PenTool, Users, Megaphone, Share2, Video, BarChart3, Palette };
const resolveIcon = (name: string) => iconMap[name] || Lightbulb;

const fallback = {
  heroLabel: "Social Media",
  heroHeadline: "Social presence that drives business.",
  heroDescription: "Strategic social media management that builds brand, engages communities, and converts followers into customers.",
  heroCta: "Discuss your social strategy",
  platforms: [
    { name: "Instagram", focus: "Visual storytelling & community" },
    { name: "LinkedIn", focus: "B2B thought leadership" },
    { name: "TikTok", focus: "Trend-driven engagement" },
    { name: "Facebook", focus: "Community & advertising" },
    { name: "Twitter/X", focus: "Real-time engagement" },
    { name: "YouTube", focus: "Long-form video content" },
  ],
  problemTitle: "Why most social strategies fail.",
  problems: [
    "Content is posted without strategy or measurable outcomes.",
    "Generic messaging fails to resonate with target audiences.",
    "Vanity metrics are tracked while business impact is ignored.",
    "Inconsistent posting kills momentum and algorithmic reach.",
  ],
  servicesLabel: "The System",
  servicesTitle: "Full-service social media management.",
  services: [
    { name: "Strategy Development", icon: "Lightbulb", components: ["Platform selection & prioritization", "Audience research & persona development", "Content pillar development"], outcome: "A clear roadmap for social media success." },
    { name: "Content Creation", icon: "PenTool", components: ["Visual content design", "Video production & editing", "Copywriting & storytelling"], outcome: "Consistent, high-quality content that builds brand equity." },
    { name: "Community Management", icon: "Users", components: ["Real-time engagement", "Comment & DM management", "Influencer relationship building"], outcome: "An engaged community that amplifies your brand." },
    { name: "Paid Social Advertising", icon: "Megaphone", components: ["Campaign strategy & setup", "Audience targeting & segmentation", "Performance reporting"], outcome: "Efficient reach and measurable business outcomes." },
  ],
  processTitle: "From strategy to sustained growth.",
  processSteps: [
    { number: "01", title: "Audit & Discovery", description: "Analyze your current social presence, audience, competitors, and opportunities for growth." },
    { number: "02", title: "Strategy Development", description: "Build a comprehensive social strategy aligned with brand goals and audience behavior." },
    { number: "03", title: "Content Planning", description: "Develop content pillars, themes, and a production calendar that sustains engagement." },
    { number: "04", title: "Execution & Management", description: "Publish, engage, and manage community interactions across all active platforms." },
    { number: "05", title: "Optimization & Reporting", description: "Measure performance, extract insights, and continuously refine the strategy." },
  ],
  techLabel: "Platforms",
  techTitle: "Tools we work with.",
  techDescription: "We're platform-agnostic and work with the tools that best serve your social strategy.",
  techCategories: [
    { title: "Social Platforms", icon: "Share2", items: ["Instagram", "TikTok", "LinkedIn", "Facebook"] },
    { title: "Content Creation", icon: "Video", items: ["Canva", "Adobe Creative", "CapCut", "Figma"] },
    { title: "Analytics", icon: "BarChart3", items: ["Sprout Social", "Hootsuite", "Later", "Brandwatch"] },
    { title: "Creative Tools", icon: "Palette", items: ["Premiere Pro", "After Effects", "Photoshop", "Illustrator"] },
  ],
  ctaTitle: "Ready to transform your social presence?",
  ctaDescription: "Let's discuss how strategic social media can drive real business results.",
  ctaCta: "Get started",
};

const SocialMedia = () => {
  const { content } = usePageContent("social-media", fallback);

  const resolvedServices = content.services.map((s: any) => ({
    ...s,
    icon: typeof s.icon === "string" ? resolveIcon(s.icon) : s.icon,
  }));
  const resolvedTech = content.techCategories.map((c: any) => ({
    ...c,
    icon: typeof c.icon === "string" ? resolveIcon(c.icon) : c.icon,
  }));

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div className="absolute top-0 right-1/4 w-[800px] h-[800px] rounded-full" variants={glowVariant} initial="hidden" animate="visible" style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)' }} />
        </div>
        <div className="container-wide relative z-10">
          <div className="max-w-3xl">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.span variants={heroVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">{content.heroLabel}</motion.span>
              <motion.h1 variants={heroVariant} className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]">{content.heroHeadline}</motion.h1>
              <motion.p variants={heroVariant} className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">{content.heroDescription}</motion.p>
              <motion.div variants={heroVariant} className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="group" asChild><Link to="/contact">{content.heroCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PLATFORMS */}
      <section className="py-8 border-t border-border bg-muted/30">
        <div className="container-wide">
          <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}>
            {content.platforms.map((p: any) => (
              <motion.div key={p.name} variants={itemVariant} className="text-center">
                <div className="text-sm font-medium text-foreground mb-1">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.focus}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-10 md:py-16 lg:py-20 border-t border-border">
        <div className="container-wide">
          <motion.div className="max-w-3xl" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30%" }} variants={problemSectionVariant}>
            <motion.div variants={problemHeaderVariant}>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">The Problem</span>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-10">{content.problemTitle}</h2>
            </motion.div>
            <motion.div className="relative" variants={problemStaggerContainer}>
              <motion.div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary/15" variants={problemAccentLineVariant} style={{ transformOrigin: "top" }} />
              <div className="space-y-5 pl-6">
                {content.problems.map((problem: string, i: number) => (
                  <motion.div key={i} variants={problemBulletVariant} className="group relative">
                    <div className="absolute -left-6 top-0 bottom-0 w-0.5 bg-transparent transition-colors duration-200 md:group-hover:bg-primary" />
                    <p className="text-foreground/80 transition-colors duration-200 md:group-hover:text-foreground">{problem}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <ServicesGrid label={content.servicesLabel} title={content.servicesTitle} services={resolvedServices} />
      <EditorialSteps title={content.processTitle} steps={content.processSteps} />
      <TechnologyStack label={content.techLabel} title={content.techTitle} description={content.techDescription} categories={resolvedTech} />

      {/* CTA */}
      <section className="py-16 md:py-24 border-t border-border bg-muted/20">
        <div className="container-wide">
          <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">{content.ctaTitle}</h2>
            <p className="text-muted-foreground mb-8">{content.ctaDescription}</p>
            <Button size="lg" className="group" asChild><Link to="/contact">{content.ctaCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMedia;
